import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    suburb: '',
    lawnSize: '',
    issues: '',
    preferredTime: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create mailto link with form data
    const subject = encodeURIComponent('Lawn Coring Quote Request');
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Mobile: ${formData.mobile}\n` +
      `Suburb: ${formData.suburb}\n` +
      `Lawn Size: ${formData.lawnSize}\n` +
      `Issues: ${formData.issues}\n` +
      `Preferred Time: ${formData.preferredTime}`
    );
    
    window.location.href = `mailto:info@perthlawncoring.com.au?subject=${subject}&body=${body}`;
    
    // Show success message
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 5000);
    
    // Reset form
    setFormData({
      name: '',
      mobile: '',
      suburb: '',
      lawnSize: '',
      issues: '',
      preferredTime: ''
    });
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1 className="display-medium">Contact Us</h1>
          <p className="body-large" style={{ marginTop: '16px', maxWidth: '700px' }}>
            Get in touch for a quick quote or any questions about our lawn coring and verti-mowing services.
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="contact-methods">
        <div className="container">
          <div className="methods-grid">
            <a href="tel:0417910217" className="method-card clickable">
              <Phone size={40} color="#004534" />
              <h3 className="heading-3" style={{ marginTop: '16px' }}>Call Us</h3>
              <p className="body-large" style={{ marginTop: '8px', color: 'var(--brand-dark)', fontWeight: '600' }}>
                0417 910 217
              </p>
              <p className="body-small" style={{ marginTop: '4px' }}>
                Fast response, often same-day service
              </p>
            </a>

            <a href="mailto:info@perthlawncoring.com.au?subject=Quote Request" className="method-card clickable">
              <Mail size={40} color="#004534" />
              <h3 className="heading-3" style={{ marginTop: '16px' }}>Email Us</h3>
              <p className="body-medium" style={{ marginTop: '8px' }}>
                info@perthlawncoring.com.au
              </p>
              <p className="body-small" style={{ marginTop: '4px' }}>
                We'll respond within 24 hours
              </p>
            </a>

            <div className="method-card">
              <MapPin size={40} color="#004534" />
              <h3 className="heading-3" style={{ marginTop: '16px' }}>Visit Us</h3>
              <p className="body-medium" style={{ marginTop: '8px' }}>
                28 Pelican Parade<br />
                Ballajura WA 6066
              </p>
              <p className="body-small" style={{ marginTop: '4px' }}>
                Servicing Northern Suburbs
              </p>
            </div>

            <div className="method-card">
              <Clock size={40} color="#004534" />
              <h3 className="heading-3" style={{ marginTop: '16px' }}>Hours</h3>
              <p className="body-medium" style={{ marginTop: '8px' }}>
                Monday - Friday<br />
                8:00 AM - 5:00 PM AWST
              </p>
              <p className="body-small" style={{ marginTop: '4px' }}>
                Saturday/Sunday by appointment
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form & Map */}
      <section className="form-map-section">
        <div className="container">
          <div className="form-map-grid">
            {/* Quote Form */}
            <div className="quote-form-card">
              <h2 className="heading-2">Request a Quote</h2>
              <p className="body-medium" style={{ marginTop: '8px' }}>
                Fill in the details below and we'll get back to you shortly.
              </p>

              {showSuccess && (
                <div className="success-message">
                  <CheckCircle size={20} color="#004534" />
                  <span>Your email client will open to send the quote request!</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="quote-form">
                <div className="form-group">
                  <label htmlFor="name" className="body-medium">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="mobile" className="body-medium">Mobile *</label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="0400 000 000"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="suburb" className="body-medium">Suburb *</label>
                  <input
                    type="text"
                    id="suburb"
                    name="suburb"
                    value={formData.suburb}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="e.g. Ballajura"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lawnSize" className="body-medium">Lawn Size Estimate</label>
                  <select
                    id="lawnSize"
                    name="lawnSize"
                    value={formData.lawnSize}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="">Select size</option>
                    <option value="Small (under 100m²)">Small (under 100m²)</option>
                    <option value="Medium (100-300m²)">Medium (100-300m²)</option>
                    <option value="Large (300-500m²)">Large (300-500m²)</option>
                    <option value="Extra Large (500m²+)">Extra Large (500m²+)</option>
                    <option value="Not sure">Not sure</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="issues" className="body-medium">Lawn Issues</label>
                  <textarea
                    id="issues"
                    name="issues"
                    value={formData.issues}
                    onChange={handleChange}
                    className="form-input"
                    rows="4"
                    placeholder="e.g. Spongy lawn, thick thatch, compaction, etc."
                  ></textarea>
                </div>

                <div className="form-group">
                  <label htmlFor="preferredTime" className="body-medium">Preferred Time to Call</label>
                  <input
                    type="text"
                    id="preferredTime"
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="e.g. Mornings, Weekdays after 3pm"
                  />
                </div>

                <button type="submit" className="btn-primary" style={{ width: '100%' }}>
                  <Send size={20} />
                  Send Quote Request
                </button>
              </form>
            </div>

            {/* Map */}
            <div className="map-card">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3383.0123456789!2d115.8972132!3d-31.8433377!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2a32b1a05b059e89%3A0xc232398bd53b3863!2s28%20Pelican%20Parade%2C%20Ballajura%20WA%206066!5e0!3m2!1sen!2sau!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0, borderRadius: '24px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Perth Lawn Coring Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Sticky CTA */}
      <div className="mobile-cta-sticky">
        <a href="tel:0417910217" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
          <Phone size={18} />
          Call Now: 0417 910 217
        </a>
      </div>
    </div>
  );
};

export default Contact;